// Application controller: owns state, camera, renderer, loop, the current
// geometry, the draw range and the animations (trace player, auto rotate).
// UI modules bind to it; they never touch the renderer directly.
import type { AppState, CameraState, CurveGeometry, CurveMeta, ExportMeta, GradientState, Manifest, Quat, StatePatch } from './types.ts';
import { createStore, type Store } from './state/store.ts';
import { defaultsFor } from './state/defaults.ts';
import { PREF, safeSet } from './state/prefs.ts';
import { GRADIENT_PRESETS } from './gradient/presets.ts';
import { isNearClosedXY } from './gradient/diag.ts';
import { arcballOrbit, aspectK, fitToView, newCamera, quatAxisAngle, quatMul, quatNormalize, resetCamera, zoomAt } from './render/camera.ts';
import type { FrameParams, Renderer, RenderStats } from './render/renderer.ts';
import { RenderLoop } from './render/loop.ts';
import { defaultExport, findCurve, findExport } from './data/manifest.ts';
import { getGeometry, prefetchIdle, type GeometryStage } from './data/loader.ts';
import { cameraToView, viewToCamera } from './state/url-state.ts';
import { type DrawRange, isFullRange } from './render/range.ts';
import { rewind, SPEEDS, stepPlayer, Ticker, type PlayerState, type Speed } from './anim.ts';
import { CURVE_DISPLAY, isHiddenCurve } from './ui/curve-meta.ts';
import type { OverlayLines, View3d } from './render/renderer.ts';
import type { RGB } from './types.ts';
import { loadTiles, locateTile, tileEdgeSegments, tileNoun, tileOutline, PRISM_EDGES, type TileSet } from './data/tiles.ts';
import { QUAQUA_RULES } from './data/generators/quaqua-rules.ts';
import { RampCache, colorAt, hexToRgb } from './gradient/ramp.ts';

export type Stage = 'none' | 'loading' | 'preview' | 'full';

export interface FrameInfo {
  stats: RenderStats;
  ms: number;
  refining: boolean;
}

export type ViewPreset = 'iso' | 'front' | 'side' | 'top';
// Same presets as the Curve Atlas page.
const VIEW_PRESETS: Record<ViewPreset, Quat> = {
  iso: quatMul(quatAxisAngle([1, 0, 0], -0.57), quatMul(quatAxisAngle([0, 1, 0], 0.62), quatAxisAngle([0, 0, 1], 0.17))),
  front: [0, 0, 0, 1],
  side: quatAxisAngle([0, 1, 0], Math.PI / 2),
  top: quatAxisAngle([1, 0, 0], -Math.PI / 2),
};
const SPIN_RAD_PER_S = 0.35;

export class App {
  readonly store: Store<AppState>;
  readonly cam: CameraState = newCamera();
  readonly loop: RenderLoop;
  readonly ticker = new Ticker();
  /** Portion of the curve drawn; mutable like the camera, surfaced via `listeners`. */
  readonly range: DrawRange = { start: 0, end: 1 };
  readonly player: PlayerState = { range: this.range, playing: false, autoNext: false, speed: 1, advanceAt: 0 };
  spin = false;
  geom: CurveGeometry | null = null;
  stage: Stage = 'none';
  cyclic = false;
  viewW = 1;
  viewH = 1;
  lastFrame: FrameInfo | null = null;
  toast: (msg: string, ms?: number) => void = () => {};
  /** Canvas background a curve switch restores; follows the UI theme (main.ts). */
  themeBg = '#ffffff';
  /** Underlying tiling of the current export, when shown and loaded. */
  tiles: TileSet | null = null;
  tileStatus = '';
  tileTipOutside = false;
  private tileEdges: Float32Array | null = null;
  private tileToken = 0;
  private tileActive = -1;
  private tileActiveId = -1;
  private tileActiveOutline: Float32Array | null = null;
  private cageSegments: Float32Array | null = null;
  private rampCache = new RampCache();
  /** Fired after geometry, frame, range or camera changes; UI uses it to refresh readouts. */
  readonly listeners = new Set<() => void>();

  private loadToken = 0;
  private curveTracker: string;
  private exportTracker: string;
  private uiTracker: boolean;
  private tilesShowTracker: boolean;

  constructor(
    readonly manifest: Manifest,
    public renderer: Renderer,
    initial: AppState,
  ) {
    this.store = createStore(initial);
    this.curveTracker = initial.curve;
    this.exportTracker = initial.exportId;
    this.uiTracker = initial.uiHidden;
    this.tilesShowTracker = initial.tiles.show;
    this.loop = new RenderLoop({
      renderer: () => this.renderer,
      frameParams: (stride) => this.frameParams(stride),
      nPoints: () => this.geom?.nPoints ?? 0,
      detail: () => this.store.get().detail,
      onFrame: (stats, ms, refining) => {
        this.lastFrame = { stats, ms, refining };
        this.emit();
      },
    });
    this.store.subscribe((s) => {
      if (s.uiHidden !== this.uiTracker) {
        // The open region changes with the chrome; a fitted view stays fitted.
        const wasFit = this.isFitted(this.uiTracker);   // judged against the region that was showing
        this.uiTracker = s.uiHidden;
        if (wasFit) { this.fit(); this.emit(); }
      }
      if (s.tiles.show !== this.tilesShowTracker) {
        this.tilesShowTracker = s.tiles.show;
        this.refreshTiles();
      }
      if (s.curve !== this.curveTracker || s.exportId !== this.exportTracker) {
        const curveChanged = s.curve !== this.curveTracker;
        this.curveTracker = s.curve;
        this.exportTracker = s.exportId;
        if (curveChanged) { this.showAll(); this.setSpin(false); }
        this.selectExport();
      } else {
        this.requestDraw();
      }
    });
  }

  // ----- selectors -----
  get state(): AppState { return this.store.get(); }
  get curve(): CurveMeta {
    return findCurve(this.manifest, this.state.curve) ?? this.manifest.curves[0];
  }
  get exp(): ExportMeta {
    const c = this.curve;
    return findExport(c, this.state.exportId) ?? defaultExport(c);
  }
  is3d(): boolean { return (this.geom?.dims ?? this.exp.dims) === 3; }
  aspectK(): number { return aspectK(this.state.aspect); }

  /** Curves offered in the grid and on the 1..n keys, in display order; hidden curves stay reachable by link. */
  orderedCurves(): CurveMeta[] {
    const rank = new Map(CURVE_DISPLAY.map((d, i) => [d.id, i]));
    return this.manifest.curves.filter((c) => !isHiddenCurve(c.id))
      .sort((a, b) => (rank.get(a.id) ?? 99) - (rank.get(b.id) ?? 99));
  }

  /** The drawing variant of an export: the second token of its label ("d5 · ribbon · tree_tour" -> "ribbon"). */
  static variantOf(e: ExportMeta): string { return e.label.split('·')[1]?.trim() ?? ''; }
  /** Distinct variants of the current curve, in order of first appearance (one entry when the curve has none). */
  variants(): string[] {
    const seen: string[] = [];
    for (const e of this.curve.exports) { const v = App.variantOf(e); if (!seen.includes(v)) seen.push(v); }
    return seen;
  }
  currentVariant(): string { return App.variantOf(this.exp); }
  /** Exports the iteration controls step through: the current variant's, or all when there is only one. */
  variantExports(): ExportMeta[] {
    const all = this.curve.exports;
    if (this.variants().length < 2) return all;
    const v = this.currentVariant();
    return all.filter((e) => App.variantOf(e) === v);
  }
  /** Switch drawing variant, keeping the depth when that variant has it (else the nearest). */
  setVariant(v: string): void {
    const list = this.curve.exports.filter((e) => App.variantOf(e) === v);
    if (!list.length || v === this.currentVariant()) return;
    const d = this.exp.depth;
    const best = list.reduce((a, b) => (Math.abs(b.depth - d) < Math.abs(a.depth - d) ? b : a));
    this.store.patch({ exportId: best.id });
  }

  /** Index of the current export within its variant list, and whether a next one exists. */
  exportIndex(): number { return this.variantExports().findIndex((e) => e.id === this.exp.id); }
  hasNextExport(): boolean { return this.exportIndex() < this.variantExports().length - 1; }

  /** Gradient state with auto-cycles resolved against the current export. */
  effectiveGrad(): GradientState {
    const s = this.state;
    const g = s.grad;
    if (!g.autoCycles) return g;
    let rec = this.exp.rec_cycles | 0 || 1;
    if (s.colorMode === 'helix') {
      // Hue turns are bounded above: past ~12 the lightness gained per turn
      // drops under the just-noticeable difference.
      rec = Math.max(6, Math.min(12, Math.round(rec / 4)));
    } else {
      rec = Math.max(1, Math.min(64, rec));
    }
    return g.repeat === rec ? g : { ...g, repeat: rec };
  }

  frameParams(stride: number): FrameParams {
    const s = this.state;
    return {
      cam: this.cam,
      viewW: this.viewW,
      viewH: this.viewH,
      aspectK: this.aspectK(),
      is3d: this.is3d(),
      lineWidth: s.width,
      bgHex: s.bgHex,
      solidHex: s.solidHex,
      colorMode: s.colorMode,
      grad: this.effectiveGrad(),
      cyclic: this.cyclic,
      transparent: false,
      stride,
      range: { start: this.range.start, end: this.range.end },
      view3d: this.view3d(),
      overlays: this.overlays(),
    };
  }

  /** Half the largest bbox span of the current export, in world units. */
  extent(): number {
    const e = this.exp;
    let m = 0;
    for (let i = 0; i < 3; i++) m = Math.max(m, (e.bbox_max[i] ?? 0) - (e.bbox_min[i] ?? 0));
    return Math.max(1e-9, m / 2);
  }

  view3d(): View3d {
    const v = this.state.view3d;
    const is3d = this.is3d();
    return { perspective: is3d && v.projection === 'persp', depthCue: is3d && !v.xray, opacity: is3d ? v.opacity : 1, extent: this.extent() };
  }

  /** Whether the current export has a tiling to show. */
  hasTiles(): boolean { return !!this.exp.tiles; }
  isQuaqua(): boolean { return this.exp.tiles?.kind === 'prisms' || this.state.curve.startsWith('quaqua'); }

  private darkCanvas(): boolean {
    const c = hexToRgb(this.state.bgHex || '#ffffff');
    return 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2] < 128;
  }

  /** Point of the curve at a fractional index. */
  pointAt(index: number): [number, number, number] {
    const g = this.geom;
    if (!g) return [0, 0, 0];
    const D = g.dims, n = g.nPoints;
    const i = Math.max(0, Math.min(n - 1, index));
    const lo = Math.floor(i), hi = Math.min(n - 1, lo + 1), t = i - lo;
    const c = g.coords;
    const at = (k: number, d: number) => (d < D ? c[k * D + d] : 0);
    return [0, 1, 2].map((d) => at(lo, d) + (at(hi, d) - at(lo, d)) * t) as [number, number, number];
  }

  private overlays(): OverlayLines[] {
    const out: OverlayLines[] = [];
    const s = this.state;
    const g = this.geom;
    if (!g) return out;
    const dark = this.darkCanvas();
    const D = g.dims;
    // tiles behind the curve
    if (s.tiles.show && this.tiles && this.tileEdges) {
      out.push({ coords: this.tileEdges, dims: this.tiles.dims, color: dark ? [166, 189, 207] : [70, 88, 105], alpha: s.tiles.opacity, width: 0.85 });
      if (s.tiles.highlight) {
        const idx = this.range.end * (g.nPoints - 1);
        const id = locateTile(this.tiles, this.pointAt(idx), idx, this.tileActive);
        this.tileActive = id;
        this.tileTipOutside = id < 0;
        if (id >= 0) {
          if (id !== this.tileActiveId || !this.tileActiveOutline) { this.tileActiveId = id; this.tileActiveOutline = tileOutline(this.tiles, id); }
          if (this.tileActiveOutline) out.push({ coords: this.tileActiveOutline, dims: this.tiles.dims, color: dark ? [214, 255, 232] : [24, 110, 72], alpha: 0.94, width: 2.2 });
        }
      } else this.tileTipOutside = false;
    }
    // prism cage
    if (this.is3d() && s.view3d.cage && this.isQuaqua()) {
      if (!this.cageSegments) {
        const v = QUAQUA_RULES.vertices;
        const seg = new Float32Array(PRISM_EDGES.length * 6);
        let k = 0;
        for (const [a, b] of PRISM_EDGES) { seg.set(v[a], k); seg.set(v[b], k + 3); k += 6; }
        this.cageSegments = seg;
      }
      out.push({ coords: this.cageSegments, dims: 3, color: dark ? [125, 153, 166] : [90, 110, 122], alpha: 0.38, width: 0.8 });
    }
    // endpoint dots, coloured like the path there
    if (s.endpoints) {
      const total = g.nPoints - 1;
      for (const t of [this.range.start, this.range.end]) {
        const p = this.pointAt(t * total);
        const coords = new Float32Array(D === 3 ? [p[0], p[1], p[2], p[0], p[1], p[2]] : [p[0], p[1], p[0], p[1]]);
        const color: RGB = s.colorMode === 'solid' ? hexToRgb(s.solidHex) : colorAt(t, this.cyclic, this.effectiveGrad(), s.colorMode, this.rampCache);
        out.push({ coords, dims: D, color, alpha: 1, width: 7 });
      }
    }
    return out;
  }

  /** (Re)load the tiling for the current export when it is shown; clears it otherwise. */
  refreshTiles(): void {
    const token = ++this.tileToken;
    this.tiles = null; this.tileEdges = null; this.tileActive = -1; this.tileActiveId = -1; this.tileActiveOutline = null; this.tileTipOutside = false;
    const exp = this.exp;
    if (!this.state.tiles.show) { this.tileStatus = exp.tiles ? 'Leaf tiles at the selected iteration.' : 'Tiling is available for the six main curves.'; this.requestDraw(); this.emit(); return; }
    if (!exp.tiles) { this.tileStatus = 'Tiling is available for the six main curves.'; this.requestDraw(); this.emit(); return; }
    if (!this.geom) { this.tileStatus = 'Preparing tile geometry…'; this.emit(); return; }
    this.tileStatus = 'Preparing tile geometry…';
    this.emit();
    const p = loadTiles(exp, this.geom);
    if (!p) return;
    p.then((t) => {
      if (token !== this.tileToken) return;
      this.tiles = t;
      this.tileEdges = tileEdgeSegments(t);
      this.tileStatus = t.n.toLocaleString() + ' ' + tileNoun(this.state.curve) + (t.kind === 'prisms' ? ' · wireframe' : '');
      this.requestDraw();
      this.emit();
    }).catch((err: unknown) => {
      if (token !== this.tileToken) return;
      this.tileStatus = 'Could not load the tiling: ' + (err instanceof Error ? err.message : String(err));
      this.emit();
    });
  }

  // ----- lifecycle -----
  emit(): void { for (const l of this.listeners) l(); }
  requestDraw(): void { this.loop.requestDraw(); }

  /**
   * Adopt the canvas's CSS size. The world point at the viewport centre and the
   * span across the shorter side are preserved, so hiding the panel or rotating
   * a phone re-centres the picture instead of shifting it.
   */
  resize(): void {
    const rect = this.renderer.canvas.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;
    const sizeChanged = rect.width !== this.viewW || rect.height !== this.viewH;
    const hadSize = this.viewW > 1 && this.viewH > 1;
    const wasFit = sizeChanged && hadSize && this.isFitted();
    if (sizeChanged && hadSize && !wasFit) {
      const v = cameraToView(this.cam, this.viewW, this.viewH, this.is3d());
      Object.assign(this.cam, viewToCamera(v, rect.width, rect.height));
    }
    this.viewW = rect.width;
    this.viewH = rect.height;
    // A view still at "fit" follows the new open region (phone rotation, panel toggles).
    if (wasFit) this.fit();
    this.renderer.resize(rect.width, rect.height, window.devicePixelRatio || 1);
    this.requestDraw();
    this.emit();
  }

  /** True when the camera is (within a pixel or so) what `fit()` would produce now. */
  isFitted(uiHidden = this.state.uiHidden): boolean {
    const tmp: CameraState = { ...this.cam };
    this.fitInto(tmp, uiHidden);
    return Math.abs(tmp.scale - this.cam.scale) <= tmp.scale * 0.005
      && Math.abs(tmp.tx - this.cam.tx) < 1.5 && Math.abs(tmp.ty - this.cam.ty) < 1.5;
  }

  // ----- camera -----
  /**
   * The part of the canvas not covered by chrome (panel, title, dock), in CSS
   * px. Fit and the zoom buttons work on this region so the curve lands in the
   * open area; the numbers mirror the layout in index.html.
   */
  viewRegion(uiHidden = this.state.uiHidden): { x: number; y: number; w: number; h: number } {
    const W = this.viewW, H = this.viewH;
    if (uiHidden) return { x: W * 0.03, y: H * 0.03, w: W * 0.94, h: H * 0.94 };
    if (W <= 720 && W > H && H <= 540) {          // phone, landscape: panel 226 px on the left
      return { x: 250, y: 140, w: Math.max(80, W - 250 - 16), h: Math.max(30, H - 140 - 146) };
    }
    if (W <= 720) {                                 // phone, portrait: panel and dock stacked at the bottom
      const bottom = H - Math.min(H * 0.38, 310) - 198;
      const top = Math.min(130, bottom - 20);
      return { x: W * 0.06, y: top, w: W * 0.88, h: Math.max(20, bottom - top) };
    }
    const left = 316 + 18, right = 24, top = 145, bottom = 152;
    return { x: left, y: top, w: Math.max(160, W - left - right - 38), h: Math.max(60, H - top - bottom) };
  }

  private fitInto(cam: CameraState, uiHidden = this.state.uiHidden): void {
    const e = this.exp;
    const r = this.viewRegion(uiHidden);
    const persp = this.is3d() && this.state.view3d.projection === 'persp' ? this.extent() : 0;
    fitToView(cam, { min: e.bbox_min, max: e.bbox_max }, r.w, r.h, this.aspectK(), this.is3d(), persp);
    cam.tx += r.x;
    cam.ty += r.y;
  }
  fit(): void { this.fitInto(this.cam); }

  /** Scale that `fit()` would produce for the current rotation; zoom is relative to it. */
  fitScale(): number {
    const tmp: CameraState = { ...this.cam };
    this.fitInto(tmp);
    return tmp.scale > 0 ? tmp.scale : 1;
  }
  zoomLevel(): number { return this.cam.scale / this.fitScale(); }

  /** Zoom about the centre of the open view region by `factor`. */
  zoomBy(factor: number): void {
    const r = this.viewRegion();
    zoomAt(this.cam, r.x + r.w / 2, r.y + r.h / 2, factor);
    this.loop.markInteracting();
    this.requestDraw();
    this.emit();
  }

  /** Fit the whole curve into the view, keeping the 3D rotation. */
  fitView(): void { this.fit(); this.requestDraw(); this.emit(); }

  resetView(): void {
    resetCamera(this.cam);
    if (this.is3d()) this.cam.rot = [...VIEW_PRESETS.iso];
    this.fit();
    this.requestDraw();
    this.emit();
  }

  setView(preset: ViewPreset): void {
    this.setSpin(false);
    this.cam.rot = [...VIEW_PRESETS[preset]];
    this.fit();
    this.requestDraw();
    this.emit();
  }

  /** Arcball drag from (x0, y0) to (x1, y1) in canvas px, about the open region's centre. */
  orbit(x0: number, y0: number, x1: number, y1: number): void {
    const r = this.viewRegion();
    const radius = Math.min(r.w, r.h) * 0.5;
    this.cam.rot = arcballOrbit(this.cam.rot, x0, y0, x1, y1, r.x + r.w / 2, r.y + r.h / 2, Math.max(40, radius));
  }

  setSpin(on: boolean): void {
    if (this.spin === on) return;
    this.spin = on;
    this.ticker.set('spin', on ? (dt) => {
      this.cam.rot = quatNormalize(quatMul(quatAxisAngle([0, 1, 0], dt * SPIN_RAD_PER_S), this.cam.rot));
      if (this.renderer.kind === 'canvas2d') this.loop.markInteracting();
      this.requestDraw();
    } : null);
    this.emit();
  }

  // ----- draw range and trace player -----
  setRange(start: number, end: number): void {
    const s = Math.max(0, Math.min(1, start));
    const e = Math.max(0, Math.min(1, end));
    this.range.start = s;
    this.range.end = Math.max(e, Math.min(1, s + 0.001));
    this.player.advanceAt = 0;
    this.requestDraw();
    this.emit();
  }
  showAll(): void { this.pause(); this.range.start = 0; this.range.end = 1; this.requestDraw(); this.emit(); }
  isFullDrawing(): boolean { return isFullRange(this.range); }

  play(): void {
    if (this.player.playing) return;
    this.player.playing = true;
    this.player.advanceAt = 0;
    if (this.range.end >= 1) rewind(this.player);
    this.ticker.set('player', (dt, now) => this.tickPlayer(dt, now));
    this.requestDraw();
    this.emit();
  }
  pause(): void {
    if (!this.player.playing) return;
    this.player.playing = false;
    this.player.advanceAt = 0;
    this.ticker.set('player', null);
    this.emit();
  }
  togglePlay(): void { if (this.player.playing) this.pause(); else this.play(); }
  replay(): void { rewind(this.player); this.player.playing = false; this.play(); }
  setSpeed(v: number): void { if ((SPEEDS as number[]).includes(v)) { this.player.speed = v as Speed; this.emit(); } }
  setAutoNext(on: boolean): void { this.player.autoNext = on; this.player.advanceAt = 0; this.emit(); }

  private tickPlayer(dt: number, now: number): void {
    if (this.stage === 'loading') return;   // wait for the next iteration's geometry
    const ev = stepPlayer(this.player, dt, now, this.hasNextExport());
    if (ev === 'advance') { this.stepExport(+1); this.emit(); return; }
    if (ev === 'finished') { this.ticker.set('player', null); this.requestDraw(); this.emit(); return; }
    if (ev === 'draw') { this.requestDraw(); this.emit(); }
  }

  // ----- state -----
  /** Apply a plate or curve-switch patch. Missing exportId means the default depth. */
  applyState(P: StatePatch): void {
    const s = this.state;
    const curveId = P.curve ?? s.curve;
    const curve = findCurve(this.manifest, curveId) ?? this.manifest.curves[0];
    const exportId = P.exportId && findExport(curve, P.exportId) ? P.exportId : defaultExport(curve).id;
    let grad: GradientState = { ...s.grad };
    if (P.stops) { grad.stops = P.stops.slice(); grad.preset = 'custom'; }
    else if (P.preset && GRADIENT_PRESETS[P.preset]) { grad.preset = P.preset; grad.stops = GRADIENT_PRESETS[P.preset].slice(); }
    if (P.closure) grad.closure = P.closure;
    if (P.rotation !== undefined) grad.rotation = P.rotation;
    if (P.repeat !== undefined) grad.repeat = P.repeat;
    grad = {
      ...grad,
      reverse: !!P.reverse,
      hard: !!P.hard,
      pingpong: P.pingpong !== false,
      autoCycles: !!P.autoCycles,
    };
    const next: AppState = {
      ...s,
      curve: curve.id,
      exportId,
      grad,
      colorMode: P.colorMode ?? s.colorMode,
      bgHex: P.bgHex ?? s.bgHex,
      solidHex: P.solidHex ?? s.solidHex,
      width: P.width ?? s.width,
      aspect: P.aspect ?? s.aspect,
      detail: P.detail ?? s.detail,
      plate: P.plate ?? null,
    };
    if (P.bgHex) safeSet(PREF.bgHex, P.bgHex);
    if (P.solidHex) safeSet(PREF.solidHex, P.solidHex);
    this.store.set(next);
  }

  switchCurve(curveId: string): void {
    if (curveId === this.state.curve) return;
    this.applyState({ curve: curveId, ...defaultsFor(curveId), bgHex: this.themeBg });
  }

  /** Step the iteration depth by +-1 within the current curve. */
  stepExport(step: number): void {
    const list = this.variantExports();
    const i = this.exportIndex();
    const next = Math.max(0, Math.min(list.length - 1, i + step));
    if (next !== i) this.store.patch({ exportId: list[next].id });
  }
  selectExportIndex(i: number): void {
    const list = this.variantExports();
    const k = Math.max(0, Math.min(list.length - 1, i | 0));
    if (list[k].id !== this.exp.id) this.store.patch({ exportId: list[k].id });
  }

  /** Load geometry for the selected export; called on curve/export change. */
  selectExport(): void {
    const exp = this.exp;
    const token = ++this.loadToken;
    this.stage = 'loading';
    this.geom = null;
    this.cyclic = false;
    this.tiles = null; this.tileEdges = null; this.tileActiveOutline = null; this.tileActive = -1; this.tileActiveId = -1; this.tileToken++;
    this.renderer.setGeometry(null);
    this.resetView();
    this.emit();
    getGeometry(exp, (g, stage) => {
      if (token !== this.loadToken) return;
      this.applyGeometry(g, stage);
    }).then(() => {
      if (token !== this.loadToken) return;
      const list = this.curve.exports;
      const i = list.findIndex((e) => e.id === exp.id);
      prefetchIdle([list[i - 1], list[i + 1]].filter((x): x is ExportMeta => !!x && x.source.kind === 'file'));
    }).catch((err: unknown) => {
      if (token !== this.loadToken) return;
      this.stage = 'none';
      this.pause();
      this.toast('Could not load ' + exp.label + ': ' + (err instanceof Error ? err.message : String(err)), 5000);
      this.emit();
    });
  }

  private applyGeometry(g: CurveGeometry, stage: GeometryStage): void {
    this.geom = g;
    this.stage = stage;
    this.cyclic = g.closed || isNearClosedXY(g.coords, g.nPoints, g.dims);
    this.renderer.setGeometry(g);
    if (stage === 'full' && this.state.tiles.show) this.refreshTiles();
    this.requestDraw();
    this.emit();
  }
}
